import { styled } from '@mui/material/styles'
import Box from '@/components/common/Box'
import {
	colors,
	typography,
	typePresets,
	spacing,
	radius,
	shadows,
	sizing,
	transitions,
	focusVisible,
} from '@/styles/designTokens'

export const CardRoot = styled('article', {
	shouldForwardProp: (prop) => prop !== 'isOutOfStock',
})<{ isOutOfStock?: boolean }>(({ theme, isOutOfStock }) => ({
	boxSizing: 'border-box',
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'stretch',
	width: '100%',
	border: `1px solid ${colors.border}`,
	borderRadius: radius.card,
	boxShadow: shadows.card,
	overflow: 'hidden',
	backgroundColor: isOutOfStock ? colors.surfaceMuted : colors.surfaceWhite,
	transition: `background-color ${transitions.fast}`,
	height: sizing.cardHeightMobile,
	minHeight: sizing.cardHeightMobile,
	maxHeight: sizing.cardHeightMobile,
	cursor: isOutOfStock ? 'default' : 'pointer',
	[theme.breakpoints.up('lg')]: {
		height: sizing.cardHeightDesktop,
		minHeight: sizing.cardHeightDesktop,
		maxHeight: sizing.cardHeightDesktop,
	},
	'&:focus-visible': {
		outline: `${focusVisible.outline} ${colors.brand}`,
		outlineOffset: focusVisible.outlineOffset,
	},
}))

export const CardBody = styled(Box)({
	flex: '1 1 auto',
	minWidth: 0,
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'center',
	padding: spacing.lg,
})

export const CardInfo = styled(Box)({
	flex: '1 1 auto',
	display: 'flex',
	flexDirection: 'column',
	gap: spacing.xs,
})

export const CardName = styled('h3', {
	shouldForwardProp: (prop) => prop !== 'isOutOfStock',
})<{ isOutOfStock?: boolean }>(({ isOutOfStock }) => ({
	margin: 0,
	fontSize: typography.fontSizeMd,
	fontWeight: typography.fontWeightBold,
	lineHeight: typography.lineHeightMd,
	letterSpacing: typography.letterSpacingWide,
	color: isOutOfStock ? colors.textSecondary : colors.textPrimary,
	display: '-webkit-box',
	WebkitLineClamp: 2,
	WebkitBoxOrient: 'vertical',
	overflow: 'hidden',
}))

export const CardDescription = styled('p', {
	shouldForwardProp: (prop) => prop !== 'isOutOfStock',
})<{ isOutOfStock?: boolean }>(({ isOutOfStock }) => ({
	margin: 0,
	fontSize: typography.fontSizeSm,
	fontWeight: typography.fontWeightRegular,
	lineHeight: typography.lineHeightSm,
	letterSpacing: typography.letterSpacingWidest,
	color: isOutOfStock ? colors.textDisabled : colors.textSecondary,
	display: '-webkit-box',
	WebkitLineClamp: 2,
	WebkitBoxOrient: 'vertical',
	overflow: 'hidden',
}))

export const CardPriceRow = styled(Box)({
	display: 'flex',
	alignItems: 'center',
	gap: spacing.sm,
	marginTop: spacing.md,
})

export const CardPrice = styled('span', {
	shouldForwardProp: (prop) => prop !== 'isOutOfStock',
})<{ isOutOfStock?: boolean }>(({ isOutOfStock }) => ({
	fontSize: typography.fontSizeMd,
	fontWeight: typography.fontWeightBold,
	lineHeight: typography.lineHeightMd,
	color: isOutOfStock ? colors.textSecondary : colors.textPrimary,
}))

export const SoldOutLabel = styled('span')({
	...typePresets.bodySm,
	fontWeight: typography.fontWeightSemiBold,
	color: colors.errorFigma,
})

export const LowStockLabel = styled('span')({
	...typePresets.bodySm,
	fontWeight: typography.fontWeightSemiBold,
	color: colors.warningFigma,
})

export const CardImageWrap = styled(Box)(({ theme }) => ({
	width: sizing.cardImageWidth,
	height: sizing.cardHeightMobile,
	flexShrink: 0,
	position: 'relative',
	backgroundColor: colors.overlayLight,
	[theme.breakpoints.up('lg')]: {
		height: sizing.cardHeightDesktop,
	},
}))

export const CardImage = styled('img')({
	width: '100%',
	height: '100%',
	objectFit: 'cover',
	display: 'block',
})

export const CardImagePlaceholder = styled(Box)({
	width: '100%',
	height: '100%',
	backgroundColor: colors.overlayLight,
})

export const AddButton = styled('button', {
	shouldForwardProp: (prop) => prop !== 'isOutOfStock',
})<{ isOutOfStock?: boolean }>(({ isOutOfStock }) => ({
	position: 'absolute',
	// 7px optical offset — aligns visually with card corner inside the border radius
	bottom: 7,
	right: 7,
	width: sizing.addButton,
	height: sizing.addButton,
	borderRadius: radius.circle,
	border: `1px solid ${colors.overlayHeavy}`,
	backgroundColor: colors.surfaceWhite,
	color: colors.textPrimary,
	fontSize: typography.fontSizeIcon,
	lineHeight: 1,
	cursor: 'pointer',
	display: isOutOfStock ? 'none' : 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	flexShrink: 0,
	padding: 0,
	'&:disabled': {
		opacity: 0.6,
		cursor: 'default',
	},
	'&:focus-visible': {
		outline: focusVisible.outline,
		outlineOffset: focusVisible.outlineOffset,
	},
}))
